import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { Link } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDate from '../../components/DisplayDate';
import CircleImage from '../../components/CircleImage';
import GlobalModal from '../../components/GlobalModal';
import ActivateModal from './ActivateModal';
import OrganisationLogo from '../../components/OrganisationLogo';

// TYPES
import { OrganisationListOrganisation, DomainOrganisationStatus } from '../../api/models';

const Companies = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showActivateModal, setShowActivateModal] = React.useState<boolean>(false);

	// Set up data state
	const [companiesData, setCompaniesData] = React.useState<OrganisationListOrganisation[]>([]);

	const getCompaniesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getCompaniesData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			setCompaniesData(response.result);
		} else {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getCompaniesData();
	}, []);

	// Filter data
	const activeCompanies = companiesData.filter((item) => item.status === DomainOrganisationStatus.Active);
	const deactiveCompanies = companiesData.filter((item) => item.status === DomainOrganisationStatus.Inactive);
	const incompleteCompanies = companiesData.filter((item) => item.status === DomainOrganisationStatus.Pending);

	// State to control active tab and active content
	const [activeTab, setActiveTab] = React.useState<string>('active');

	// Activate organisation
	const [organisationDetail, setOrganisationDetail] = React.useState<OrganisationListOrganisation>({});

	const activateOrganisation = async (item: OrganisationListOrganisation) => {
		setOrganisationDetail(item);

		setShowActivateModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="page-header-section mtop-40">
									<div className="header-left">
										<Button
											onClick={() => setActiveTab('active')}
											className={`staff-category pdl-0 ${activeTab === 'active' ? 'active' : ''}`}
										>
											Active
										</Button>

										<Button
											onClick={() => setActiveTab('deactive')}
											className={`staff-category ${activeTab === 'deactive' ? 'active' : ''}`}
										>
											Deactivated
										</Button>

										<Button
											onClick={() => setActiveTab('incomplete')}
											className={`staff-category ${activeTab === 'incomplete' ? 'active' : ''}`}
										>
											Incomplete
										</Button>
									</div>
								</div>

								{activeTab === 'active' ? (
									<section className="info-section">
										<ul>
											{activeCompanies &&
												activeCompanies.length > 0 &&
												activeCompanies.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0">
																	<OrganisationLogo src={item.logo} size={40} />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-lg-6 detail-name">{item.name}</div>

																		<div className="col-lg-6 detail-status">{item.organisationType}</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 pdr-0" to={`/companies/${item.id}`}>
																		View
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									</section>
								) : null}

								{activeTab === 'deactive' ? (
									<section className="info-section">
										<ul>
											{deactiveCompanies &&
												deactiveCompanies.length > 0 &&
												deactiveCompanies.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0">
																	<CircleImage src={item.logo} />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-lg-5 detail-name">{item.name}</div>

																		<div className="col-lg-3 detail-status">
																			<div className="name color-grey-4">{item.organisationType}</div>
																		</div>

																		<div className="col-lg-4 detail-status">
																			{item.statusDate && (
																				<>
																					<strong>Date Deactivated</strong> &nbsp;
																					<DisplayDate>{item.statusDate}</DisplayDate>
																				</>
																			)}
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 remove pdr-0" to={`/companies/${item.id}`}>
																		View
																	</Link>

																	<Button onClick={() => activateOrganisation(item)} className="btn-text mtop-0 pdr-0">
																		Activate
																	</Button>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									</section>
								) : null}

								{activeTab === 'incomplete' ? (
									<section className="info-section">
										<ul>
											{incompleteCompanies &&
												incompleteCompanies.length > 0 &&
												incompleteCompanies.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0">
																	<CircleImage src={item.logo} />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-lg-6 detail-name">{item.name}</div>

																		<div className="col-lg-6 detail-status">
																			<div className="name color-grey-4">{item.organisationType}</div>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 remove pdr-0" to={`/companies/${item.id}`}>
																		View
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									</section>
								) : null}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showActivateModal} setIsOpen={setShowActivateModal} titleText="Enable organisation">
				<ActivateModal
					organisationDetail={organisationDetail}
					getCompaniesData={getCompaniesData}
					setActiveTab={setActiveTab}
				/>
			</GlobalModal>
		</div>
	);
};

export default Companies;
